import React, { useRef, useEffect } from 'react';
import { TiltRootContainer } from './Component.style';

export default function ImageTilt({
  width = 520,
  height = 600,
  maxTilt = 12, // degrees
  depth = 28, // px for translateZ/box shadow multiplier
  scale = 1.03, // zoom on hover
  ease = 0.12, // lerp smoothing (0 - 1)
  src
}) {
  const rootRef = useRef(null);
  const innerRef = useRef(null);
  const glareRef = useRef(null);
  const state = useRef({
    targetX: 0,
    targetY: 0,
    currentX: 0,
    currentY: 0,
    rafId: null,
  });

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    function onMove(e) {
      const r = root.getBoundingClientRect();
      const x = (e.clientX - r.left) / r.width - 0.5; // -0.5 .. 0.5
      const y = (e.clientY - r.top) / r.height - 0.5; // -0.5 .. 0.5

      state.current.targetX = x;
      state.current.targetY = y;

      if (!state.current.rafId) {
        state.current.rafId = requestAnimationFrame(animate);
      }

      if (glareRef.current) {
        const gx = (x + 0.5) * 100;
        const gy = (y + 0.5) * 100;
        glareRef.current.style.background = `radial-gradient(600px 200px at ${gx}% ${gy}%, rgba(255,255,255,0.35), rgba(255,255,255,0.04) 30%, rgba(255,255,255,0) 60%)`;
      }
    }

    function onLeave() {
      state.current.targetX = 0;
      state.current.targetY = 0;
      if (!state.current.rafId) {
        state.current.rafId = requestAnimationFrame(animate);
      }
      if (glareRef.current) {
        glareRef.current.style.background =
          'radial-gradient(600px 200px at 50% 50%, rgba(255,255,255,0.18), rgba(255,255,255,0.02) 40%, rgba(255,255,255,0) 70%)';
      }
    }

    root.addEventListener('mousemove', onMove);
    root.addEventListener('mouseleave', onLeave);
    root.addEventListener('mouseenter', onMove);

    return () => {
      root.removeEventListener('mousemove', onMove);
      root.removeEventListener('mouseleave', onLeave);
      root.removeEventListener('mouseenter', onMove);
      cancelAnimationFrame(state.current.rafId);
    };
    // eslint-disable-next-line
  }, [maxTilt, depth, scale, ease]);

  function animate() {
    const s = state.current;
    s.currentX += (s.targetX - s.currentX) * ease;
    s.currentY += (s.targetY - s.currentY) * ease;

    const rotateY = (s.currentX * maxTilt).toFixed(3);
    const rotateX = (-s.currentY * maxTilt).toFixed(3);
    const distance = Math.min(1, Math.abs(s.currentX) + Math.abs(s.currentY));
    const translateZ = distance * depth;

    if (innerRef.current) {
      innerRef.current.style.transform = `perspective(1000px) translateZ(${translateZ}px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale(${scale})`;
      innerRef.current.style.boxShadow = `${-s.currentX * 20}px ${
        s.currentY * 20 + 8
      }px ${18 + distance * depth}px rgba(10,20,40,0.18)`;
    }

    const moving =
      Math.abs(s.targetX - s.currentX) > 0.0005 ||
      Math.abs(s.targetY - s.currentY) > 0.0005;
    if (moving) {
      s.rafId = requestAnimationFrame(animate);
    } else {
      s.currentX = s.targetX;
      s.currentY = s.targetY;
      s.rafId = null;
      if (s.targetX === 0 && s.targetY === 0 && innerRef.current) {
        innerRef.current.style.transform =
          'perspective(1000px) translateZ(0px) rotateX(0deg) rotateY(0deg) scale(1)';
        innerRef.current.style.boxShadow = '0 10px 30px rgba(10,20,40,0.12)';
      }
    }
  }

  return (
    <TiltRootContainer
      ref={rootRef}
      style={{ height: `${height}px`, padding: 10 }}
      aria-hidden="true"
    >
      <div className="tilt-inner" ref={innerRef}>
        <img src={src} alt="tilt" draggable="false" className="tilt-img" />
        <div className="tilt-glare" ref={glareRef} />
      </div>
    </TiltRootContainer>
  );
}
